import { useState } from 'react'
import { Check, Clock, ClipboardCopy, Mail, MailWarning } from 'lucide-react'
import { Button, Chip } from '@/components/ui'
import type { TriageItem } from '../api/types'
import {
  ASSISTANT_STATUS_LABEL,
  ASSISTANT_STATUS_VARIANT,
  CATEGORY_LABEL,
  INBOX_LABEL,
  STATUS_LABEL,
  STATUS_VARIANT,
  TYPE_LABEL,
  TYPE_VARIANT,
} from '../utils/triage-meta'
import { AssistantPanel } from './AssistantPanel'

interface Props {
  item: TriageItem
  onResolve: (id: string) => void
  onSnooze: (id: string, hours: 1 | 4 | 24) => void
  onAssign: (id: string) => void
  onOpenInMailDesk: (threadId: string) => void
  /** Sprint 35 chunk 9 — retry button handler. */
  onRunAssistant: (id: string) => void
  /** Whether the current user can re-run the assistant (admin/ops). */
  canRunAssistant: boolean
  busy?: boolean
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso
  const minutesAgo = Math.floor((Date.now() - t) / 60_000)
  if (minutesAgo < 1) return 'just now'
  if (minutesAgo < 60) return `${minutesAgo} min ago`
  const hoursAgo = Math.floor(minutesAgo / 60)
  if (hoursAgo < 24) return `${hoursAgo}h ago`
  const daysAgo = Math.floor(hoursAgo / 24)
  return `${daysAgo}d ago`
}

export function TriageActionCard({
  item,
  onResolve,
  onSnooze,
  onAssign,
  onOpenInMailDesk,
  onRunAssistant,
  canRunAssistant,
  busy,
}: Props) {
  const isCold = item.body_status === 'cold'
  const inboxLabels = item.inboxes.map((i) => INBOX_LABEL[i]).join(' + ')

  return (
    <article className="rounded-md border border-border-subtle bg-surface p-4 shadow-sm">
      {/* Header row: priority chip + customer/topic */}
      <header className="flex items-start gap-3 border-b border-border-subtle pb-3">
        <Chip size="md" variant={TYPE_VARIANT[item.email_type]} dot>
          {TYPE_LABEL[item.email_type]}
        </Chip>
        {item.status !== 'open' ? (
          <Chip size="md" variant={STATUS_VARIANT[item.status]}>
            {STATUS_LABEL[item.status]}
          </Chip>
        ) : null}
        {/*
         * Sprint 35 chunks 5–8 — CARLA inbox-assistant state chip.
         * Renders only when the Orders / Support agent has touched
         * the row. Hover tooltip shows the assistant_summary excerpt.
         * The "↪ Support" follow-up indicator (chunk 9) folds into
         * this same chip when assistant_status='escalated' AND the
         * summary explicitly mentions a Support handoff.
         */}
        {item.assistant_processed_at && item.assistant_status ? (
          <Chip
            size="md"
            variant={ASSISTANT_STATUS_VARIANT[item.assistant_status]}
            title={item.assistant_summary ?? undefined}
          >
            {item.assistant_status === 'escalated' &&
            item.assistant_summary?.includes('Routed to Support')
              ? '↪ Support'
              : ASSISTANT_STATUS_LABEL[item.assistant_status]}
          </Chip>
        ) : null}
        <div className="min-w-0 flex-1">
          <h3 className="text-role-data-primary truncate">
            {item.customer_name ?? item.sender_email ?? '(unknown sender)'}
            {item.subject ? ` — ${item.subject}` : ''}
          </h3>
          <p className="text-role-metadata mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <Mail size={12} /> {inboxLabels}
            </span>
            {item.is_cross_inbox ? (
              <Chip size="xs" variant="info">
                Cross-inbox
              </Chip>
            ) : null}
            <span className="inline-flex items-center gap-1">
              <Clock size={12} /> {formatRelative(item.latest_msg_at)}
            </span>
            <span>{CATEGORY_LABEL[item.category]}</span>
            {item.so_number ? <span>SO {item.so_number}</span> : null}
          </p>
        </div>
      </header>

      {/* Body: CARLA summary or fallback */}
      <div className="py-3">
        {item.summary ? (
          <p className="text-role-data-secondary">{item.summary}</p>
        ) : (
          <p className="text-role-metadata italic">
            (no summary — classification pending)
          </p>
        )}
        {isCold ? (
          <p className="text-role-metadata mt-2 inline-flex items-center gap-1.5">
            <MailWarning size={12} />
            Preview only — open in MailDesk for the full body.
          </p>
        ) : null}
        {item.draft_reply_text ? (
          <DraftReplyBlock draft={item.draft_reply_text} />
        ) : null}
        <AssistantPanel
          item={item}
          onRunAssistant={onRunAssistant}
          canRetry={canRunAssistant}
          busy={busy}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border-subtle pt-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onOpenInMailDesk(item.thread_id)}
          disabled={busy}
        >
          Open in MailDesk
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onAssign(item.id)}
          disabled={busy}
        >
          Assign
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onSnooze(item.id, 1)}
          disabled={busy}
        >
          Snooze 1h
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => onResolve(item.id)}
          disabled={busy}
        >
          Mark Resolved
        </Button>
      </div>
    </article>
  )
}


function DraftReplyBlock({ draft }: { draft: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = async () => {
    if (!navigator.clipboard) return
    try {
      await navigator.clipboard.writeText(draft)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // best-effort; some browsers block clipboard in non-secure contexts
    }
  }
  return (
    <div className="mt-3 rounded-md border border-info-border bg-info-bg p-3">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-role-section-label">Draft tracking reply</span>
        <Button variant="ghost" size="sm" onClick={handleCopy}>
          {copied ? (
            <>
              <Check size={12} /> Copied
            </>
          ) : (
            <>
              <ClipboardCopy size={12} /> Copy
            </>
          )}
        </Button>
      </div>
      <pre className="text-role-data-secondary whitespace-pre-wrap break-words font-sans">
        {draft}
      </pre>
    </div>
  )
}
